import { ContractStatusEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import type { Position } from 'uikit';

import { useMemo, useCallback, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export enum TooltipNameWorkOrderKebabMenu {
    CreateWorkOrderChange = 'CreateWorkOrderChange',
    ReplaceVOR = 'ReplaceVOR',
    DeleteWorkOrder = 'DeleteWorkOrder',
    DownloadTemplateActKC2 = 'DownloadTemplateActKC2',
    UploadTemplateActKC2 = 'UploadTemplateActKC2',
    PreviousVersion = 'PreviousVersion',
}

interface IUseAddItemsWorkOrderKebabMenu {
    workOrderId: number;
    statusContract?: ContractStatusEnum;
    statusWorkOrder?: WorkOrderStatusEnum;
    isNotActKC2: boolean;
    isWorkOrderVorTypeXml: boolean;
    handleDownloadWorkOrder: () => void;
    handleDownloadWorkOrderPrices: () => void;
    openCreatingWorkOrderChangeHandler: () => void;
    openCreateClosingWorkOrderChangeHandler: () => void;
    handleDeleteButton: () => void;
    exportActKC2TemplateOpen: () => void;
    importActKC2TemplateOpen: () => void;
    openRollbackWorkOrderHandler: () => void;
    projectId?: string;
    isDisabledByProjectStatus?: boolean;
    canRollbackWorkOrderChange: boolean;
    isHasOrderChangeValidStatus: boolean;
}

export const useAddItemsWorkOrderKebabMenu = ({
    projectId,
    workOrderId,
    statusContract,
    statusWorkOrder,
    isWorkOrderVorTypeXml,
    isDisabledByProjectStatus,
    handleDownloadWorkOrder,
    handleDownloadWorkOrderPrices,
    openCreatingWorkOrderChangeHandler,
    openRollbackWorkOrderHandler,
    handleDeleteButton,
    exportActKC2TemplateOpen,
    importActKC2TemplateOpen,
    canRollbackWorkOrderChange,
    isHasOrderChangeValidStatus,
    openCreateClosingWorkOrderChangeHandler,
}: IUseAddItemsWorkOrderKebabMenu) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { aktKC2: aktKC2Rules, order: orderRules, workOrderChangeUer: workOrderChangeUerRules } = useAccess();

    const workOrderKebabMenuGroups = useMemo(
        () => [
            {
                id: 1,
            },
            {
                id: 2,
            },
        ],
        [],
    );

    const isContractStatusWork = statusContract === ContractStatusEnum.InImplementation;
    const isWorkOrderStatusOnFormation = statusWorkOrder === WorkOrderStatusEnum.OnFormation;
    const isWorkOrderStatusInProgress = statusWorkOrder === WorkOrderStatusEnum.InProgress;
    const isWorkOrderStatusDelayed = statusWorkOrder === WorkOrderStatusEnum.Delayed;
    const isWorkOrderStatusCompleted = statusWorkOrder === WorkOrderStatusEnum.Completed;

    const isWorkOrderDeleteDisabled =
        !isContractStatusWork || !orderRules.canDelete || isDisabledByProjectStatus || !isWorkOrderStatusOnFormation;

    const isWorkOrderItemDisabled = !isContractStatusWork || isDisabledByProjectStatus || !isWorkOrderStatusInProgress;

    const isChooseFileVorDisabled =
        !isContractStatusWork || !isWorkOrderStatusOnFormation || isDisabledByProjectStatus || !isWorkOrderVorTypeXml;

    const workOrderKebabMenuItems = useMemo(
        () => [
            {
                label: 'Выгрузить Наряд-Заказ',
                action: handleDownloadWorkOrder,
                id: 1,
                groupId: 1,
                disabled: !orderRules.canExportExcel,
            },
            {
                label: 'Заменить файл ВОР',
                action: () =>
                    navigate(`/projects/uer/${projectId}/order/${workOrderId}/replace`, {
                        state: location.pathname,
                    }),
                id: 2,
                groupId: 1,
                disabled: isChooseFileVorDisabled,
                haveTooltip: !isWorkOrderVorTypeXml || isWorkOrderStatusInProgress,
                tooltipName: TooltipNameWorkOrderKebabMenu.ReplaceVOR,
            },
            {
                label: 'Создать Изм. к НЗ',
                action: openCreatingWorkOrderChangeHandler,
                id: 3,
                groupId: 1,
                disabled: isWorkOrderItemDisabled || !workOrderChangeUerRules.canCreate || isHasOrderChangeValidStatus,
                haveTooltip:
                    isWorkOrderStatusOnFormation ||
                    isHasOrderChangeValidStatus ||
                    isWorkOrderStatusCompleted ||
                    isWorkOrderStatusDelayed,
                tooltipName: TooltipNameWorkOrderKebabMenu.CreateWorkOrderChange,
            },
            {
                label: 'Создать закрывающий Изм. к НЗ',
                id: 9,
                groupId: 1,
                action: openCreateClosingWorkOrderChangeHandler,
                disabled: !isWorkOrderStatusInProgress || !workOrderChangeUerRules?.canCreateClosing,
            },
            {
                label: 'Вернуть Наряд-Заказ к предыдущей версии',
                action: openRollbackWorkOrderHandler,
                id: 8,
                groupId: 1,
                disabled: !canRollbackWorkOrderChange || !orderRules.canRollbackWorkOrderChange,
                haveTooltip: isWorkOrderStatusCompleted || isWorkOrderStatusDelayed,
                tooltipName: TooltipNameWorkOrderKebabMenu.PreviousVersion,
            },
            {
                id: 4,
                label: 'Выгрузить накопительную ведомость по НЗ',
                groupId: 1,
                action: handleDownloadWorkOrderPrices,
                disabled: !(isWorkOrderStatusInProgress || isWorkOrderStatusCompleted),
                haveTooltip: false,
            },
            {
                label: 'Выгрузить шаблон Акта',
                id: 5,
                groupId: 1,
                action: exportActKC2TemplateOpen,
                disabled: isWorkOrderItemDisabled || !aktKC2Rules.canUploadActTemplate,
                haveTooltip: isWorkOrderStatusOnFormation || isWorkOrderStatusCompleted || isWorkOrderStatusDelayed,
                tooltipName: TooltipNameWorkOrderKebabMenu.DownloadTemplateActKC2,
            },
            {
                label: 'Загрузить шаблон Акта',
                id: 6,
                groupId: 1,
                action: importActKC2TemplateOpen,
                disabled:
                    isWorkOrderItemDisabled ||
                    !aktKC2Rules.canDownloadActTemplate ||
                    isWorkOrderStatusCompleted ||
                    isWorkOrderStatusDelayed,
                haveTooltip: isWorkOrderStatusOnFormation || isWorkOrderStatusCompleted || isWorkOrderStatusDelayed,
                tooltipName: TooltipNameWorkOrderKebabMenu.UploadTemplateActKC2,
            },
            {
                label: 'Удалить Наряд-Заказ',
                action: handleDeleteButton,
                id: 7,
                groupId: 1,
                disabled: isWorkOrderDeleteDisabled,
                haveTooltip: isWorkOrderStatusInProgress || isWorkOrderStatusCompleted || isWorkOrderStatusDelayed,
                tooltipName: TooltipNameWorkOrderKebabMenu.DeleteWorkOrder,
            },
        ],
        [
            aktKC2Rules.canDownloadActTemplate,
            aktKC2Rules.canUploadActTemplate,
            canRollbackWorkOrderChange,
            exportActKC2TemplateOpen,
            handleDeleteButton,
            handleDownloadWorkOrder,
            handleDownloadWorkOrderPrices,
            importActKC2TemplateOpen,
            isChooseFileVorDisabled,
            isHasOrderChangeValidStatus,
            isWorkOrderDeleteDisabled,
            isWorkOrderItemDisabled,
            isWorkOrderStatusCompleted,
            isWorkOrderStatusDelayed,
            isWorkOrderStatusInProgress,
            isWorkOrderStatusOnFormation,
            isWorkOrderVorTypeXml,
            location.pathname,
            navigate,
            openCreateClosingWorkOrderChangeHandler,
            openCreatingWorkOrderChangeHandler,
            openRollbackWorkOrderHandler,
            orderRules.canExportExcel,
            orderRules.canRollbackWorkOrderChange,
            projectId,
            workOrderChangeUerRules.canCreate,
            workOrderChangeUerRules?.canCreateClosing,
            workOrderId,
        ],
    );

    const getMenuItemAttributes = useCallback((item: { haveTooltip: boolean; tooltipName: string }) => {
        if (item.haveTooltip) {
            if (item.tooltipName === TooltipNameWorkOrderKebabMenu.PreviousVersion) {
                return {
                    readOnly: true,
                    name: TooltipNameWorkOrderKebabMenu.PreviousVersion,
                };
            }
            if (item.tooltipName === TooltipNameWorkOrderKebabMenu.CreateWorkOrderChange) {
                return {
                    readOnly: true,
                    name: TooltipNameWorkOrderKebabMenu.CreateWorkOrderChange,
                };
            }
            if (item.tooltipName === TooltipNameWorkOrderKebabMenu.ReplaceVOR) {
                return {
                    readOnly: true,
                    name: TooltipNameWorkOrderKebabMenu.ReplaceVOR,
                };
            }
            if (item.tooltipName === TooltipNameWorkOrderKebabMenu.DeleteWorkOrder) {
                return {
                    readOnly: true,
                    name: TooltipNameWorkOrderKebabMenu.DeleteWorkOrder,
                };
            }
            if (item.tooltipName === TooltipNameWorkOrderKebabMenu.DownloadTemplateActKC2) {
                return {
                    readOnly: true,
                    name: TooltipNameWorkOrderKebabMenu.DownloadTemplateActKC2,
                };
            }
            if (item.tooltipName === TooltipNameWorkOrderKebabMenu.UploadTemplateActKC2) {
                return {
                    readOnly: true,
                    name: TooltipNameWorkOrderKebabMenu.UploadTemplateActKC2,
                };
            }
        }

        return undefined;
    }, []) as any;

    const [tooltipPosition, setTooltipPosition] = useState<Position>(undefined);
    const [activeTooltip, setActiveTooltip] = useState<{ [key: string]: boolean }>({});

    const onMenuMouseMove = useCallback((event) => {
        const attr = event.target.getAttributeNames();
        const hasTooltip = attr.includes('readonly');
        const target = event.target.getAttribute('name');

        const rect = event.currentTarget.getBoundingClientRect();
        const targetRect = event.target.getBoundingClientRect();

        const heightItemMenu = 24;

        if (hasTooltip) {
            if (target === TooltipNameWorkOrderKebabMenu.PreviousVersion) {
                setActiveTooltip({ [TooltipNameWorkOrderKebabMenu.PreviousVersion]: true });
            }
            if (target === TooltipNameWorkOrderKebabMenu.CreateWorkOrderChange) {
                setActiveTooltip({ [TooltipNameWorkOrderKebabMenu.CreateWorkOrderChange]: true });
            }
            if (target === TooltipNameWorkOrderKebabMenu.ReplaceVOR) {
                setActiveTooltip({ [TooltipNameWorkOrderKebabMenu.ReplaceVOR]: true });
            }
            if (target === TooltipNameWorkOrderKebabMenu.DeleteWorkOrder) {
                setActiveTooltip({ [TooltipNameWorkOrderKebabMenu.DeleteWorkOrder]: true });
            }
            if (target === TooltipNameWorkOrderKebabMenu.DownloadTemplateActKC2) {
                setActiveTooltip({ [TooltipNameWorkOrderKebabMenu.DownloadTemplateActKC2]: true });
            }
            if (target === TooltipNameWorkOrderKebabMenu.UploadTemplateActKC2) {
                setActiveTooltip({ [TooltipNameWorkOrderKebabMenu.UploadTemplateActKC2]: true });
            }

            setTooltipPosition({ x: rect.right, y: targetRect.bottom - heightItemMenu / 2 });
        } else {
            setActiveTooltip({});
        }
    }, []);

    const onMenuMouseLeave = useCallback(() => {
        setActiveTooltip({});
    }, []);

    return {
        isHasOrderChangeValidStatus,
        workOrderKebabMenuGroups,
        workOrderKebabMenuItems,
        getMenuItemAttributes,
        activeTooltip,
        tooltipPosition,
        onMenuMouseMove,
        onMenuMouseLeave,
    };
};
